import { EventObject } from '@xstate/store-react';
import { StoreContext } from '../../../models/store/context';
import { log } from '@domgen/shared/src/utils/log';
import { AppStatus } from '../../../enums/appStatus';
import { StoreEvent } from '../../../enums/storeEvent';
import { saveSession } from '../../../services/session/save';
import { store } from '../../store';
import { CatalogueCategory } from '../../../models/services/api/getCatalogue';

/**
 * @description Set Brand Catalogue
 * This event is responsible for setting the brand catalogue in the store context when it is received from the API. It also checks if there are URL parameters present in the context, and if so, it triggers processing of those URL parameters which may further filter the catalogue or navigate the user to a specific page. Finally, it saves the updated session context after setting the catalogue.
 * @param {StoreContext} context - The current store context
 * @param {EventObject & { data: CatalogueCategory[] }} event - The event object containing the brand catalogue data in event.data
 * @param {{ effect: (effect: () => Promise<void>) => void }} enqueue - An object with an effect function to enqueue side effects
 * @returns {StoreContext} Updated context with brand catalogue set and URL parameters processed if present
 */
export const setBrandCatalogue = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: CatalogueCategory[] },
  enqueue: { effect: (effect: () => Promise<void>) => void },
): StoreContext => {
  log.debug({ message: StoreEvent.SET_CATALOGUE, data: event.data });
  const hasUrlParams = !!(context.urlParams?.mn || context.urlParams?.sn || context.urlParams?.rs);

  if (hasUrlParams) {
    enqueue.effect(async () => {
      store.send({ type: StoreEvent.PROCESS_URL_PARAMS });
    });
  }

  const updatedContext = {
    ...context,
    app: {
      ...context.app,
      catalogue: event.data,
      status: hasUrlParams ? AppStatus.LOADING : AppStatus.READY,
    },
  } as StoreContext;

  saveSession(updatedContext);
  return updatedContext;
};
